import { useState, useEffect, useCallback } from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@waitlist/ui';
import { Download, Users, RefreshCw, Mail, Globe, Calendar, Tag, Trash2, AlertTriangle } from 'lucide-react';
import { fetchProducts, fetchWaitlist, deleteWaitlistEntry, type Product, type WaitlistEntry } from './api';

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function exportToCSV(entries: WaitlistEntry[], product: string): void {
  const headers = ['Email', 'Source', 'UTM Source', 'UTM Campaign', 'Date'];
  const rows = entries.map((e) => [
    e.email,
    e.source || '',
    e.utmSource || '',
    e.utmCampaign || '',
    new Date(e.date).toISOString(),
  ]);

  const csv = [headers.join(','), ...rows.map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))].join('\n');

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${product}-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Confirmation Dialog Component
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  email: string;
}

function ConfirmDialog({ isOpen, onClose, onConfirm, title, message, email }: ConfirmDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center">
          <AlertTriangle className="mr-2 h-6 w-6 text-red-500" />
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
        <p className="mb-2 text-gray-600">{message}</p>
        <p className="mb-6 font-mono text-sm text-gray-800">{email}</p>
        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm} className="bg-red-600 hover:bg-red-700">
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState<string>('');
  const [deleting, setDeleting] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const data = await fetchProducts();
      setProducts(data);
      if (data.length > 0 && !selectedProduct) {
        setSelectedProduct(data[0].name);
      }
    } catch (err) {
      setError('Failed to load products');
    }
  }, [selectedProduct]);

  const loadWaitlist = useCallback(async () => {
    if (!selectedProduct) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWaitlist(selectedProduct);
      setEntries(data.entries);
      setCount(data.count);
    } catch (err) {
      setError('Failed to load waitlist data');
    } finally {
      setLoading(false);
    }
  }, [selectedProduct]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadWaitlist();
  }, [loadWaitlist]);

  const handleDeleteClick = (email: string) => {
    setDeleteEmail(email);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteEmail || !selectedProduct) return;
    
    setDeleting(true);
    try {
      await deleteWaitlistEntry(selectedProduct, deleteEmail);
      // Remove from local state
      setEntries((prev) => prev.filter((e) => e.email !== deleteEmail));
      setCount((prev) => prev - 1);
      setDeleteDialogOpen(false);
      setDeleteEmail('');
    } catch (err) {
      setError('Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteEmail('');
  };

  const filteredEntries = entries.filter((e) =>
    e.email.toLowerCase().includes(search.toLowerCase()) ||
    (e.source?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (e.utmSource?.toLowerCase() || '').includes(search.toLowerCase()) ||
    (e.utmCampaign?.toLowerCase() || '').includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Waitlist Admin</h1>
            <p className="mt-1 text-gray-600">Manage and export waitlist signups</p>
          </div>
          <Button variant="outline" onClick={loadWaitlist} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="mb-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Total Signups</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="mr-2 h-5 w-5 text-blue-600" />
                <span className="text-3xl font-bold">{count.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Product</CardTitle>
            </CardHeader>
            <CardContent>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {products.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name} ({p.count})
                  </option>
                ))}
              </select>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Export</CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => exportToCSV(entries, selectedProduct)}
                disabled={entries.length === 0}
                className="w-full"
              >
                <Download className="mr-2 h-4 w-4" />
                Download CSV
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Waitlist Entries</CardTitle>
              <Input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-64"
              />
            </div>
          </CardHeader>
          <CardContent>
            {filteredEntries.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                {search ? 'No matches found' : 'No entries yet'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        <Mail className="mr-1 inline h-4 w-4" />
                        Email
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        <Globe className="mr-1 inline h-4 w-4" />
                        Source
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        <Tag className="mr-1 inline h-4 w-4" />
                        UTM Source
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        <Tag className="mr-1 inline h-4 w-4" />
                        UTM Campaign
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">
                        <Calendar className="mr-1 inline h-4 w-4" />
                        Date
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredEntries.map((entry, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{entry.email}</td>
                        <td className="px-4 py-3 text-gray-600">{entry.source || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{entry.utmSource || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{entry.utmCampaign || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(entry.date)}</td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(entry.email)}
                            disabled={deleting}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-4 text-sm text-gray-600">
              Showing {filteredEntries.length} of {entries.length} entries
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Entry"
        message="Are you sure you want to delete this waitlist entry?"
        email={deleteEmail}
      />
    </div>
  );
}

export default App;
